import { createContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';

import type {
  AuthContextValue,
  AuthStatus,
  AdminAuthUser,
} from '../types/auth';
import { getSession, logout as logoutRequest } from '../services/authService';

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined,
);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>('checking');
  const [user, setUser] = useState<AdminAuthUser | null>(null);

  const refreshSession = useCallback(async (): Promise<boolean> => {
    try {
      const session = await getSession();

      if (!session) {
        setUser(null);
        setStatus('unauthenticated');

        return false;
      }

      setUser(session.user);
      setStatus('authenticated');

      return true;
    } catch (error) {
      console.error('Failed to check admin session:', error);

      setUser(null);
      setStatus('unauthenticated');

      return false;
    }
  }, []);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  const logout = useCallback(async () => {
    await logoutRequest();

    setUser(null);
    setStatus('unauthenticated');
  }, []);

  const value: AuthContextValue = {
    status,
    user,
    refreshSession,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
